import React from 'react';
import { useTranslation } from 'react-i18next';
import { X, Printer, CheckCircle } from 'lucide-react';
import { formatDOP } from '../lib/currency';

// Datos fiscales de la empresa que aparecen en el comprobante.
// Sin NCF todavía (comprobante interno, no factura fiscal electrónica) —
// se agrega cuando la empresa tramite esa autorización ante la DGII.
const COMPANY = {
  name: 'RedMobility',
  legalName: 'RedMobility SRL',
  rnc: 'RNC: 133628181',
  address: 'Hermanas Mirabal Km. 10, No. 568, Apto. Plaza Norte, Sector Buena Vista II, Santo Domingo Norte, República Dominicana',
  web: 'www.redmobility.net',
  email: 'soporte@redmobility.net',
  phone: '+1 809 555 0100',
  logoUrl: `${process.env.PUBLIC_URL}/images/logo.png`,
};

// Estados de reserva que implican pago ya cobrado (no hay campo de gateway
// expuesto al frontend todavía, así que el estado de la reserva es la única
// señal real disponible de que el cobro se completó).
const PAID_STATUSES = ['confirmed', 'active', 'completed'];

const STATUS_LABEL = {
  es: {
    pending: 'Pendiente de pago',
    confirmed: 'Reserva confirmada',
    active: 'En curso',
    completed: 'Completada',
    cancelled: 'Cancelada',
    rejected: 'Rechazada',
  },
  en: {
    pending: 'Payment pending',
    confirmed: 'Booking confirmed',
    active: 'In progress',
    completed: 'Completed',
    cancelled: 'Cancelled',
    rejected: 'Rejected',
  },
};

// Escapa texto que se interpola en el HTML de la ventana de impresión
// (nombres de vehículo/cliente/proveedor no son de confianza).
const esc = (v) => String(v ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export default function InvoiceModal({ booking, user, onClose }) {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language?.startsWith('en');
  const locale = isEn ? 'en-US' : 'es-DO';
  const lang = isEn ? 'en' : 'es';

  const issuedDate = new Date(booking.created_at || booking.start_datetime);
  const startDate = new Date(booking.start_datetime);
  const endDate = new Date(startDate.getTime() + (booking.end_hours || 0) * 3600000);
  const insurance = booking.insurance_price || 0;
  // La tarifa de servicio se muestra solo cuando la comisión se cobró al
  // cliente (backend: commission_in_total). Congelado (default) → sin fee,
  // idéntico a producción.
  const serviceFee = booking.commission_in_total ? (booking.platform_commission || 0) : 0;
  const rental = (booking.total_price || 0) - insurance - serviceFee;
  const receiptId = booking.booking_code;
  const isPaid = PAID_STATUSES.includes(booking.status);
  const statusLabel = STATUS_LABEL[lang][booking.status] || booking.status;
  const vehicleImage = booking.asset?.images?.[0]?.url;

  const rows = [
    { label: t('invoice.rental'), value: rental },
    ...(serviceFee > 0 ? [{ label: t('invoice.serviceFee'), value: serviceFee }] : []),
    ...(insurance > 0 ? [{ label: t('invoice.insurance'), value: insurance }] : []),
  ];

  // Fecha larga sin ambigüedad (mes en texto) + hora sin segundos.
  const fmtDate = (d) => d.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
  const fmtTime = (d) => d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });

  const handlePrint = () => {
    const logoAbsUrl = `${window.location.origin}${COMPANY.logoUrl}`;

    const card = (label, rowsHtml) => `
    <div class="card">
      <div class="card-label">${label}</div>
      <table>${rowsHtml}</table>
    </div>`;

    const row = (label, value, opts = {}) =>
      `<tr${opts.bold ? ' class="total"' : ''}><td>${label}</td><td class="num">${value}</td></tr>`;

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${t('invoice.title')} ${receiptId}</title>
<style>
  @page{size:A4;margin:14mm}
  *{box-sizing:border-box}
  body{font-family:"Segoe UI",Arial,Helvetica,sans-serif;color:#1A1A2E;margin:0;font-size:13px}
  .num{font-variant-numeric:tabular-nums;text-align:right}

  header{background:#fff;border-bottom:3px solid #134168;padding:8px 0 16px;
    display:flex;justify-content:space-between;align-items:center;margin-bottom:20px}
  header img{height:110px;object-fit:contain}
  .doc-title{text-align:right}
  .doc-title h1{font-size:15px;margin:0;letter-spacing:.04em;font-weight:700;color:#134168}
  .status-badge{display:inline-flex;align-items:center;gap:6px;margin-top:6px;
    background:${isPaid ? '#1a9c5a' : '#6b7280'};color:#fff;font-size:11px;font-weight:700;
    padding:4px 10px;border-radius:20px;letter-spacing:.03em}

  .meta{display:flex;justify-content:space-between;border:1px solid #e5e7eb;border-radius:10px;
    padding:14px 22px;margin-bottom:18px}
  .meta-item .label{text-transform:uppercase;font-size:10px;letter-spacing:.08em;color:#8b93a3;margin-bottom:3px}
  .meta-item .value{font-weight:700;font-size:14px;color:#134168}
  .meta-item .sub{font-size:11.5px;color:#6b7280;margin-top:1px}

  .section-title{font-size:11px;text-transform:uppercase;letter-spacing:.09em;color:#134168;
    font-weight:800;border-bottom:2px solid #eef0f4;padding-bottom:6px;margin:18px 0 10px}

  .reservation{display:flex;gap:14px;border:1px solid #e5e7eb;border-radius:10px;padding:14px}
  .reservation img{width:96px;height:72px;object-fit:cover;border-radius:8px;flex-shrink:0;background:#f3f4f6}
  .reservation-info{flex:1;display:flex;justify-content:space-between;gap:16px}
  .vehicle-name{font-weight:700;font-size:14.5px;color:#134168}
  .vehicle-type{font-size:11.5px;color:#6b7280;text-transform:capitalize}
  .rsv-dates{font-size:12px;color:#374151;text-align:right;line-height:1.7}
  .rsv-dates b{color:#1A1A2E}

  .cards-row{display:flex;gap:16px}
  .card{flex:1;border:1px solid #e5e7eb;border-radius:10px;padding:14px}
  .card-label{text-transform:uppercase;font-size:10.5px;letter-spacing:.08em;color:#8b93a3;font-weight:700;margin-bottom:8px}
  table{width:100%;border-collapse:collapse;font-size:12.5px}
  td{padding:6px 0;border-bottom:1px solid #f1f2f5;color:#374151}
  tr.total td{border-top:1.5px solid #1A1A2E;border-bottom:none;padding-top:10px;font-weight:800;font-size:15px;color:#134168}

  .help{background:#f7f9fc;border-radius:10px;padding:14px 18px;margin-top:16px;font-size:12px;color:#4b5563}
  .help b{color:#134168}
  .help-row{display:flex;gap:24px;margin-top:8px;flex-wrap:wrap}

  .footer{border-top:1px solid #eef0f4;margin-top:18px;padding-top:12px;font-size:10.5px;color:#8b93a3;
    display:flex;justify-content:space-between}
</style></head><body>

  <header>
    <img src="${logoAbsUrl}" alt="RedMobility" />
    <div class="doc-title">
      <h1>${t('invoice.title').toUpperCase()}</h1>
      <div class="status-badge">${isPaid ? '✓' : '•'} ${statusLabel.toUpperCase()}</div>
    </div>
  </header>

  <div class="meta">
    <div class="meta-item">
      <div class="label">${t('invoice.receiptId')}</div>
      <div class="value">${receiptId}</div>
      <div class="sub">${statusLabel}</div>
    </div>
    <div class="meta-item" style="text-align:right">
      <div class="label">${isEn ? 'Issue date' : 'Fecha de emisión'}</div>
      <div class="value">${fmtDate(issuedDate)}</div>
      <div class="sub">${fmtTime(issuedDate)}</div>
    </div>
  </div>

  <div class="section-title">${t('invoice.reservationDetails')}</div>
  <div class="reservation">
    ${vehicleImage ? `<img src="${esc(vehicleImage)}" alt="" />` : ''}
    <div class="reservation-info">
      <div>
        <div class="vehicle-name">${esc(booking.asset?.name || '—')}</div>
        <div class="vehicle-type">${esc(booking.asset?.type || '')}</div>
        <div style="margin-top:8px;font-size:12px;color:#374151">
          ${t('invoice.provider')}: <b>${esc(booking.provider?.name || '—')}</b>
        </div>
      </div>
      <div class="rsv-dates">
        ${t('invoice.startDate')}: <b>${fmtDate(startDate)}, ${fmtTime(startDate)}</b><br>
        ${isEn ? 'End' : 'Fin'}: <b>${fmtDate(endDate)}, ${fmtTime(endDate)}</b><br>
        ${t('invoice.duration')}: <b>${booking.end_hours} ${t('invoice.hours')}</b>
      </div>
    </div>
  </div>

  <div class="section-title">${isEn ? 'Client' : 'Cliente'}</div>
  <div style="font-weight:700;font-size:14px;color:#134168">${esc(booking.client_name || user?.name || '—')}</div>

  <div class="section-title">${t('invoice.priceBreakdown')} · ${t('invoice.payment')}</div>
  <div class="cards-row">
    ${card(t('invoice.priceBreakdown'), [
        ...rows.map(r => row(r.label, formatDOP(r.value))),
        row(t('invoice.totalDOP'), formatDOP(booking.total_price), { bold: true }),
      ].join(''))}
    ${card(t('invoice.payment'), [
        row(t('invoice.paymentMethod'), t('invoice.paidVia')),
        row(t('invoice.amountPaid'), formatDOP(booking.total_price)),
      ].join(''))}
  </div>

  <div class="help">
    <b>${isEn ? 'Need help with your booking?' : '¿Necesitas ayuda con tu reserva?'}</b>
    <div class="help-row">
      <span>${COMPANY.email}</span>
      <span>${COMPANY.phone}</span>
      <span>${COMPANY.web}</span>
    </div>
  </div>

  <div class="footer">
    <span>${COMPANY.legalName} · ${COMPANY.rnc}</span>
    <span>${COMPANY.address}</span>
  </div>

<script>window.onload = function(){ window.print(); };</script>
</body></html>`;
    const win = window.open('', '_blank', 'width=820,height=960');
    if (win) {
      win.document.write(html);
      win.document.close();
      win.focus();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-3xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Encabezado */}
        <div className="rounded-t-3xl p-6 pb-3 border-b-2 border-[#134168] flex items-start justify-between">
          <img src={COMPANY.logoUrl} alt="RedMobility" className="h-16 object-contain" />
          <button
            onClick={onClose}
            aria-label={t('invoice.close')}
            className="p-2 hover:bg-gray-100 rounded-full transition min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <div className="px-6 pt-4 pb-2 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-[#134168]">{t('invoice.title')}</h2>
            <p className="text-xs text-gray-500 mt-1">
              {t('invoice.receiptId')}: <span className="font-mono">{receiptId}</span>
              {' · '}{fmtDate(issuedDate)}
            </p>
          </div>
          <span className={`text-[11px] font-bold px-3 py-1 rounded-full text-white ${isPaid ? 'bg-green-600' : 'bg-gray-400'}`}>
            {statusLabel.toUpperCase()}
          </span>
        </div>

        {/* Detalle de la reserva */}
        <div className="px-6 pb-4 pt-2">
          <div className="border border-gray-200 rounded-2xl p-4 space-y-2 text-sm flex gap-3">
            {vehicleImage && (
              <img src={vehicleImage} alt="" className="w-20 h-16 object-cover rounded-xl shrink-0" />
            )}
            <div className="flex-1">
              <p className="font-bold text-base">{booking.asset?.name || t('bookingConfirmation.vehicle')}</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-gray-600 mt-1">
                <span>{t('invoice.startDate')}</span>
                <span className="text-right font-medium text-gray-900">{fmtDate(startDate)}, {fmtTime(startDate)}</span>
                <span>{t('invoice.duration')}</span>
                <span className="text-right font-medium text-gray-900">{booking.end_hours} {t('invoice.hours')}</span>
                {booking.provider?.name && (<>
                  <span>{t('invoice.provider')}</span>
                  <span className="text-right font-medium text-gray-900">{booking.provider.name}</span>
                </>)}
                <span>{t('invoice.client')}</span>
                <span className="text-right font-medium text-gray-900">{booking.client_name || user?.name}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Desglose de precio */}
        <div className="px-6 pb-4">
          <h3 className="text-sm font-bold text-gray-700 mb-2">{t('invoice.priceBreakdown')}</h3>
          <div className="space-y-1.5 text-sm">
            {rows.map((r) => (
              <div key={r.label} className="flex justify-between">
                <span className="text-gray-600">{r.label}</span>
                <span className="font-medium">{formatDOP(r.value)}</span>
              </div>
            ))}
            <div className="flex justify-between pt-2 border-t border-gray-200 text-base font-bold">
              <span>{t('invoice.totalDOP')}</span>
              <span className="text-[#134168]">{formatDOP(booking.total_price)}</span>
            </div>
          </div>
        </div>

        {/* Pago */}
        <div className="px-6 pb-4">
          <div className="bg-gray-50 rounded-2xl p-4 flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 shrink-0" />
            <div className="text-sm">
              <p className="font-medium">{t('invoice.paidVia')}</p>
              <p className="text-gray-500 text-xs">
                {t('invoice.amountPaid')}: <span className="font-semibold text-gray-800">{formatDOP(booking.total_price)}</span>
              </p>
            </div>
          </div>
        </div>

        {/* Datos de la empresa */}
        <div className="px-6 pb-4 text-xs text-gray-400 leading-relaxed">
          <p className="font-semibold text-gray-500">{COMPANY.legalName} · {COMPANY.rnc}</p>
          <p>{COMPANY.address}</p>
          <p>{COMPANY.web} · {COMPANY.email}</p>
        </div>

        {/* Acciones */}
        <div className="flex gap-3 p-6 pt-2">
          <button
            onClick={onClose}
            className="flex-1 border border-gray-200 py-3 rounded-full font-bold text-gray-600 hover:bg-gray-50 transition"
          >
            {t('invoice.close')}
          </button>
          <button
            onClick={handlePrint}
            className="flex-1 bg-[#134168] text-white py-3 rounded-full font-bold hover:bg-[#39b4aa] transition flex items-center justify-center gap-2"
          >
            <Printer className="w-4 h-4" /> {t('invoice.print')}
          </button>
        </div>
      </div>
    </div>
  );
}
